import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const StoriesCarousel = () => {
  const [currentStory, setCurrentStory] = useState(0);

  const stories = [
    {
      name: 'Ramesh Kumar',
      before: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      after: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      story: '10 years begging at traffic signals',
      transformation: 'Now owns a successful tea stall',
      quote: 'I never imagined I could run my own business. This foundation gave me hope and skills.'
    },
    {
      name: 'Sunita Devi',
      before: 'https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      after: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      story: 'Single mother begging with 2 children',
      transformation: 'Now works as a trained cook in a restaurant',
      quote: 'My children go to school now. We have a home, and I have dignity.'
    },
    {
      name: 'Arjun Singh',
      before: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      after: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      story: 'Homeless for 5 years after losing job',
      transformation: 'Now manages organic farming project',
      quote: 'From the streets to leading a farming initiative - this is what second chances look like.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStory((prev) => (prev + 1) % stories.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextStory = () => {
    setCurrentStory((prev) => (prev + 1) % stories.length);
  };

  const prevStory = () => {
    setCurrentStory((prev) => (prev - 1 + stories.length) % stories.length);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Stories of Change</h2>
          <p className="text-xl text-gray-600">
            Real transformations, real hope
          </p>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStory}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-50 rounded-2xl p-8 md:p-12"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {stories[currentStory].name}
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                          Before
                        </span>
                        <span className="text-gray-700">{stories[currentStory].story}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          After
                        </span>
                        <span className="text-gray-700">{stories[currentStory].transformation}</span>
                      </div>
                    </div>
                  </div>
                  <blockquote className="text-lg italic text-gray-700 border-l-4 border-orange-500 pl-6">
                    "{stories[currentStory].quote}"
                  </blockquote>
                </div>
                
                <div className="flex space-x-6">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">Before</h4>
                    <img
                      src={stories[currentStory].before}
                      alt="Before transformation"
                      className="w-full h-64 object-cover rounded-lg shadow-md"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">After</h4>
                    <img
                      src={stories[currentStory].after}
                      alt="After transformation"
                      className="w-full h-64 object-cover rounded-lg shadow-md"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <button
            onClick={prevStory}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiChevronLeft} className="w-6 h-6 text-gray-700" />
          </button>
          
          <button
            onClick={nextStory}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiChevronRight} className="w-6 h-6 text-gray-700" />
          </button>

          {/* Dots */}
          <div className="flex justify-center mt-8 space-x-2">
            {stories.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStory(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentStory ? 'bg-orange-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoriesCarousel;